import * as React from 'react';
import { toast } from 'sonner';
import { mensagemDoErro } from '@/data/http';
import type { BuildResumo } from '@/data/types';
import { ROTULO_STATUS, type AcaoStatus } from '@/lib/capabilities';
import { useAvisoHomologacao } from '@/features/incidentes/aviso-homologacao';
import { MotivoDialog } from './acoes-build';
import { aplicarStatus } from './aplicar-status';

/**
 * Ações rápidas de homologação a partir de uma lista (menu "⋯" do feed).
 *
 * Mesmas regras do detalhe: reprovar pede motivo (diálogo), homologar versão com
 * incidente aberto/mitigado pede "Homologar mesmo assim". O resultado volta
 * para a lista pelo barramento (`aplicarStatus` emite). Os diálogos são
 * montados UMA vez por tela, não por entrada.
 */
export function useAcoesRapidas() {
  const aviso = useAvisoHomologacao();
  const [motivoPara, setMotivoPara] = React.useState<{ build: BuildResumo; acao: AcaoStatus } | null>(null);
  const [emAcao, setEmAcao] = React.useState<string | null>(null);

  async function executar(build: BuildResumo, acao: AcaoStatus) {
    if (acao.exigeMotivo) {
      setMotivoPara({ build, acao });
      return;
    }
    setEmAcao(build.versao);
    try {
      if (acao.destino === 'homologado' && build.incidentes_abertos > 0) {
        const confirmado = await aviso.confirmar([{ versao: build.versao, produto: build.produto }]);
        if (!confirmado) return;
      }
      await aplicarStatus(build, acao.destino, null);
      toast.success(`Versão ${build.versao} marcada como ${ROTULO_STATUS[acao.destino].toLowerCase()}.`);
    } catch (causa) {
      toast.error(
        `Não foi possível alterar ${build.versao}: ${mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : String(causa))}`,
      );
    } finally {
      setEmAcao(null);
    }
  }

  const dialogos = (
    <>
      {aviso.dialogo}
      <MotivoDialog
        build={motivoPara?.build ?? { versao: '', produto: '' }}
        acao={motivoPara?.acao ?? null}
        aoFechar={() => setMotivoPara(null)}
        aoConcluir={() => setMotivoPara(null)}
      />
    </>
  );

  return { executar, emAcao, dialogos };
}
